import { cn, Table, type TableClasses, type TableColumn } from "@kyan/ui";
import { AnimatePresence, motion } from "motion/react";
import { isValidElement, type ReactNode } from "react";

import { type RfqEntry } from "#/app/(desktop)/layouts/PositionsDrawer/components/Rfq/Rfq.types";
import { EmptyState } from "#/components/Contents/components/EmptyState/EmptyState";
import { useAutoExpandRows } from "#/hooks/useAutoExpandRows";
import { usePositionsDrawerMarket } from "#/store/app/positionsDrawer/positionsDrawerMarket";

import { outerColumns } from "./RfqTable.config";

type RenderFunc = (row: RfqEntry) => ReactNode;

export type RfqTableVariant = "inner" | "outmost";

export interface RfqTableProps {
  data: RfqEntry[];
  children: ReactNode | RenderFunc;
  columns?: TableColumn<RfqEntry>[];
  variant?: RfqTableVariant;
  classes?: TableClasses<RfqEntry>;
  shouldAutoExpand?: boolean;
}

export const RfqTable: React.FC<RfqTableProps> = ({
  data,
  children,
  columns = outerColumns,
  variant,
  classes,
  shouldAutoExpand = true,
}) => {
  const { market } = usePositionsDrawerMarket();
  const { openRowIds, onRowClick } = useAutoExpandRows<RfqEntry>({
    dataLength: data.reduce((acc, item) => acc + (item.count ?? 0), 0),
    shouldAutoExpand,
  });

  const isOutmost = variant === "outmost";
  const isEmpty = !data.length;

  return (
    <Table
      data={data}
      columns={columns}
      hideHeader={!isOutmost}
      openRowIds={openRowIds}
      emptyState={
        <EmptyState
          underlying={market.underlying}
          description={`Your ${market.underlying} RFQ responses will show here`}
        />
      }
      classes={{
        wrapper: cn({
          "flex flex-1 flex-col overflow-y-auto": isOutmost,
          "h-full": isEmpty,
        }),
        table: cn("flex h-full flex-1 flex-col self-start", {
          "border-b-0": isOutmost,
        }),
        thead: cn({
          "bg-background border-divider sticky top-0 z-10 border-b": isOutmost,
        }),
        tbody: cn("text-textPrimary flex flex-1 flex-col", {
          "justify-center": isEmpty,
        }),
        row: ({ row: { count }, isRowOpen }) => {
          return cn("transition-colors", {
            "first:border-t-0 last:border-b": isOutmost,
            "cursor-pointer": (count ?? 0) > 0,
            "bg-buttonHover": isRowOpen && (count ?? 0) > 0,
          });
        },
        ...classes,
      }}
      onRowClick={onRowClick}
      renderRowExpansion={({ row, isOpen }) => (
        <AnimatePresence>
          {isOpen ? (
            <motion.div
              exit={{ height: 0 }}
              initial={{ height: 0 }}
              className="overflow-hidden"
              animate={{ height: "auto" }}
              transition={{ duration: 0.3 }}
            >
              {isValidElement(children)
                ? children
                : (children as RenderFunc)(row)}
            </motion.div>
          ) : null}
        </AnimatePresence>
      )}
    />
  );
};
